<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useArdhisasaAuth } from 'dashboard/composables/useArdhisasaAuth';
import ArdhisasaAuthModal from './components/ArdhisasaAuthModal.vue';
import TicketDetail from './components/TicketDetail.vue';
import CreateTicketModal from './components/CreateTicketModal.vue';

const { t } = useI18n();

const TABS = [
  { key: 'MY', label: t('ARDHISASA.TICKETS.TABS.MY') },
  { key: 'UNASSIGNED', label: t('ARDHISASA.TICKETS.TABS.UNASSIGNED') },
  { key: 'ONGOING', label: t('ARDHISASA.TICKETS.TABS.ONGOING') },
  { key: 'CLOSED', label: t('ARDHISASA.TICKETS.TABS.CLOSED') },
];

const ELAPSED_FILTERS = [
  { key: '', label: 'All time' },
  { key: '1h', label: 'Last hour' },
  { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '3m', label: 'Last 3 months' },
  { key: '1y', label: 'Last year' },
  { key: 'older', label: 'Older than 1 year' },
];

const SORT_OPTIONS = [
  { key: 'desc', label: 'Newest first' },
  { key: 'asc', label: 'Oldest first' },
];

const activeTab = ref('MY');
const page = ref(1);
const search = ref('');
const elapsedFilter = ref('');
const sortOrder = ref('desc');
const showDetail = ref(false);
const showCreate = ref(false);
let searchTimer = null;

const { showAuth, handleAuthenticated, dismissAuth, store } =
  useArdhisasaAuth();

const currentMeta = computed(
  () =>
    store.ticketsMetaByTab[activeTab.value] || {
      count: 0,
      next: null,
      previous: null,
    }
);

function statusStyle() {
  return { background: '#71481d', color: '#ffffff' };
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function elapsed(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

const ELAPSED_MS = {
  '1h': 3600000,
  '24h': 86400000,
  '7d': 7 * 86400000,
  '30d': 30 * 86400000,
  '3m': 90 * 86400000,
  '1y': 365 * 86400000,
};

const filteredTickets = computed(() => {
  let list = store.ticketsByTab[activeTab.value] || [];
  if (elapsedFilter.value) {
    list = list.filter(ticket => {
      if (!ticket.date_opened) return false;
      const ms = Date.now() - new Date(ticket.date_opened).getTime();
      if (elapsedFilter.value === 'older') return ms > ELAPSED_MS['1y'];
      return ms <= ELAPSED_MS[elapsedFilter.value];
    });
  }
  return list;
});

async function loadTickets() {
  await store.fetchTickets(
    activeTab.value,
    page.value,
    search.value,
    false,
    sortOrder.value
  );
  store.fetchLookups();
}

function load() {
  loadTickets();
}

onMounted(() => {
  if (store.isAuthenticated) {
    loadTickets();
    store.prefetchAll();
  }
});

watch(
  () => store.isAuthenticated,
  val => {
    if (val) loadTickets();
  }
);

watch(sortOrder, () => {
  page.value = 1;
  load();
});

function selectTab(key) {
  activeTab.value = key;
  page.value = 1;
  load();
}

function prevPage() {
  if (currentMeta.value.previous) {
    page.value -= 1;
    load();
  }
}

function nextPage() {
  if (currentMeta.value.next) {
    page.value += 1;
    load();
  }
}

function onSearch() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    page.value = 1;
    load();
  }, 400);
}

async function openTicket(ticket) {
  showDetail.value = true;
  await store.fetchTicketDetail(ticket.id);
}

function onCreated() {
  showCreate.value = false;
  load();
}
</script>

<template>
  <div class="flex flex-col flex-1 w-full h-full overflow-hidden">
    <!-- Auth modal -->
    <ArdhisasaAuthModal
      v-if="showAuth"
      @authenticated="handleAuthenticated"
      @close="dismissAuth"
    />

    <!-- Ticket detail panel -->
    <TicketDetail
      v-if="showDetail && store.selectedTicket"
      :ticket="store.selectedTicket"
      :loading="store.uiFlags.fetchingDetail"
      @close="showDetail = false"
    />

    <!-- Create ticket modal -->
    <CreateTicketModal
      v-if="showCreate"
      @close="showCreate = false"
      @created="onCreated"
    />

    <!-- Page header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('ARDHISASA.TICKETS.TITLE') }}
      </h1>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#71481d] text-white text-sm font-medium hover:bg-[#5a3915]"
        @click="showCreate = true"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('ARDHISASA.TICKETS.NEW_TICKET') }}
      </button>
    </div>

    <!-- Tabs + Filters + Search -->
    <div
      class="flex items-center justify-between px-6 py-3 border-b border-n-weak flex-shrink-0 gap-4"
    >
      <div class="flex items-center gap-1">
        <button
          v-for="tab in TABS"
          :key="tab.key"
          class="px-3 py-1.5 text-xs font-medium rounded-full transition-colors"
          :class="
            activeTab === tab.key
              ? 'bg-[#71481d] text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0">
        <!-- Sort order -->
        <select
          v-model="sortOrder"
          class="text-xs rounded-lg border border-n-weak bg-white text-n-slate-12 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option v-for="s in SORT_OPTIONS" :key="s.key" :value="s.key">
            {{ s.label }}
          </option>
        </select>

        <!-- Elapsed filter -->
        <select
          v-model="elapsedFilter"
          class="text-xs rounded-lg border border-n-weak bg-white text-n-slate-12 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option v-for="f in ELAPSED_FILTERS" :key="f.key" :value="f.key">
            {{ f.label }}
          </option>
        </select>

        <!-- Search -->
        <div class="relative w-56">
          <span
            class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-11"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('ARDHISASA.TICKETS.SEARCH_PLACEHOLDER')"
            class="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            @input="onSearch"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading -->
      <div
        v-if="store.uiFlags.fetchingTickets"
        class="flex items-center justify-center h-40"
      >
        <span
          class="i-lucide-loader-circle size-6 text-n-slate-11 animate-spin"
        />
      </div>

      <!-- Empty -->
      <div
        v-else-if="!filteredTickets.length"
        class="flex items-center justify-center h-40 text-sm text-n-slate-11"
      >
        {{ t('ARDHISASA.TICKETS.EMPTY') }}
      </div>

      <!-- List -->
      <table v-else class="w-full text-sm">
        <thead class="sticky top-0 bg-white dark:bg-n-solid-2 z-10">
          <tr class="border-b border-n-weak">
            <th
              class="text-left px-6 py-3 text-xs font-semibold text-n-slate-11"
            >
              {{ t('ARDHISASA.TICKETS.TABLE.REFERENCE') }}
            </th>
            <th
              class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11"
            >
              {{ t('ARDHISASA.TICKETS.TABLE.DATE') }}
            </th>
            <th
              class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11"
            >
              {{ t('ARDHISASA.TICKETS.TABLE.ELAPSED') }}
            </th>
            <th
              class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11"
            >
              {{ t('ARDHISASA.TICKETS.TABLE.STATUS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ticket in filteredTickets"
            :key="ticket.reference_number"
            class="border-b border-n-weak hover:bg-n-alpha-2 cursor-pointer transition-colors"
            @click="openTicket(ticket)"
          >
            <td class="px-6 py-3 font-medium text-[#71481d]">
              {{ ticket.reference_number }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ formatDate(ticket.date_opened) }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ elapsed(ticket.date_opened) }}
            </td>
            <td class="px-4 py-3">
              <button
                class="text-xs font-semibold px-3 py-1 rounded-full cursor-pointer"
                :style="statusStyle(ticket.status)"
              >
                {{ ticket.status }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="currentMeta.count > 0"
      class="flex items-center justify-between px-6 py-3 border-t border-n-weak flex-shrink-0"
    >
      <p class="text-xs text-n-slate-11">
        {{
          t('ARDHISASA.TICKETS.PAGINATION.SUMMARY', {
            count: currentMeta.count,
            page,
          })
        }}
      </p>
      <div class="flex gap-2">
        <button
          :disabled="!currentMeta.previous"
          class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
          @click="prevPage"
        >
          {{ t('ARDHISASA.TICKETS.PAGINATION.PREVIOUS') }}
        </button>
        <button
          :disabled="!currentMeta.next"
          class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
          @click="nextPage"
        >
          {{ t('ARDHISASA.TICKETS.PAGINATION.NEXT') }}
        </button>
      </div>
    </div>
  </div>
</template>
